import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Enable AI QC on staging only on Thursdays and Fridays,
// between 2pm and 12:30am PHT
export const shouldEnableAIQualityCheck = () => {
  const now = dayjs().tz("Asia/Manila");
  const dayOfWeek = now.day(); // 0 = Sunday, 1 = Monday, ..., 4 = Thursday, 5 = Friday
  const hour = now.hour();
  const minute = now.minute();
  const currentTime = hour * 60 + minute; // Convert to minutes for easier comparison
  const startTime = 14 * 60; // 2pm = 14:00 = 840 minutes
  const endTime = 0 * 60 + 30; // 12:30am = 00:30 = 30 minutes

  // Thursday window: 2pm Thursday to 12:30am Friday
  // Friday window: 2pm Friday to 12:30am Saturday

  if (dayOfWeek === 4) {
    // Thursday: check if time >= 2pm
    return currentTime >= startTime;
  } else if (dayOfWeek === 5) {
    // Friday: check if time < 12:30am (part of Thursday's window) OR time >= 2pm (Friday's window)
    return currentTime < endTime || currentTime >= startTime;
  } else if (dayOfWeek === 6) {
    // Saturday early morning: check if it's before 12:30am (still part of Friday's window)
    return currentTime < endTime;
  }

  return false;
};

const STORAGE_KEY = "aiqc:resolved";

export const normalize = (value) =>
  (value ?? "").toString().trim().split(" ").join("_").toLowerCase();

const readStore = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch (error) {
    console.warn("AI QC: failed to read resolved issues from storage", error);
    return {};
  }
};

const writeStore = (store) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn("AI QC: failed to persist resolved issues", error);
  }
};

export const loadResolvedSet = (workflowId) => {
  if (!workflowId) {
    return new Set();
  }

  const store = readStore();
  const entries = store[workflowId];
  return Array.isArray(entries) ? new Set(entries) : new Set();
};

export const saveResolvedSet = (workflowId, set) => {
  if (!workflowId) {
    return;
  }

  const store = readStore();
  store[workflowId] = Array.from(set);
  writeStore(store);
};

export const makeIssueKey = ({ category, entity, startTime }) =>
  `${normalize(category)}:${normalize(entity)}:${startTime}`;

// A flag is "awaiting a second pass" once it has been reopened but no 2nd-pass verdict is in yet.
// Such a flag is treated as unresolved so it returns to Open and re-blocks completion.
export const isAwaitingSecondPass = (feedbackRow) =>
  !!(feedbackRow?.reopened_for_second_pass && !feedbackRow?.second_pass_verdict);

// Single source of truth for "resolved": the server-side editor feedback list.
// Drawer + complete gate + recheck gate all derive their resolved set from this,
// so what you see, what blocks completion, and what blocks re-validation can't diverge.
// A reopened-awaiting-2nd-pass row is excluded, so reopening pushes the flag back to pending.
export const buildFeedbackResolvedSet = (validationFeedback) =>
  new Set(
    (validationFeedback ?? [])
      .filter((f) => !isAwaitingSecondPass(f))
      .map((f) =>
        makeIssueKey({ category: f.category, entity: f.entity, startTime: f.start_time })
      )
  );

// Map issue-key -> latest feedback row, so the drawer can show 1st-pass detail on the Resolved
// tab and the 2nd-pass state on a reopened flag.
export const buildFeedbackByKey = (validationFeedback) => {
  const map = new Map();
  (validationFeedback ?? []).forEach((f) => {
    map.set(
      makeIssueKey({ category: f.category, entity: f.entity, startTime: f.start_time }),
      f
    );
  });
  return map;
};

// Group ALL feedback rows per issue key, oldest -> newest, so the panel can show the full
// selection history when a flag was acted on more than once (rows are append-only server-side).
export const buildFeedbackHistoryByKey = (validationFeedback) => {
  const map = new Map();
  (validationFeedback ?? []).forEach((f) => {
    const key = makeIssueKey({ category: f.category, entity: f.entity, startTime: f.start_time });
    const list = map.get(key) || [];
    list.push(f);
    map.set(key, list);
  });
  for (const list of map.values()) {
    list.sort((a, b) => (a.feedback_timestamp || 0) - (b.feedback_timestamp || 0));
  }
  return map;
};

// "Who + when" for a feedback selection. feedback_timestamp is epoch-ms. Rows submitted before
// attribution shipped carry no user_email; callers show a graceful fallback.
export const feedbackActor = (row) => (row?.user_email || "").trim();
export const feedbackSecondPassActor = (row) => (row?.second_pass_user_email || "").trim();
export const formatFeedbackWhen = (ms) => (ms ? dayjs(ms).format("D MMM YYYY, h:mm A") : "");

export const addResolved = (workflowId, key) => {
  if (!workflowId) {
    return;
  }

  const existing = loadResolvedSet(workflowId);
  if (!existing.has(key)) {
    existing.add(key);
    saveResolvedSet(workflowId, existing);
  }
};

export const removeResolvedSet = (workflowId) => {
  if (!workflowId) {
    return;
  }

  const store = readStore();
  delete store[workflowId];
  writeStore(store);
};

export const reasons = [
  "Misheard Audio",
  "Wrong Spelling",
  "Wrong Name / Identity",
  "Context Misunderstood",
  "Other",
];

export const reasonBtnSx = (active, isDarkMode) => ({
  borderRadius: 2,
  padding: "6px 12px",
  fontSize: "14px",
  minWidth: 0,
  border: `1px solid ${
    active ? "#2563EB" : isDarkMode ? "#52525B" : "#D1D1D1"
  }`,
  color: active ? "#2563EB" : isDarkMode ? "#FFF" : "#111827",
  bgcolor: active ? (isDarkMode ? "#0A1E46" : "#EEF6FF") : "transparent",

  "&:hover": {
    color: active ? "#2563EB" : isDarkMode ? "#FFF" : "#1C1C1C",
    bgcolor: active
      ? isDarkMode
        ? "#0A1E46"
        : "#EEF6FF"
      : isDarkMode
      ? "#31343B"
      : "#EDEDED",
    borderColor: active ? "#2563EB" : isDarkMode ? "#52525B" : "#D1D1D1",
  },
});
